// Generic util functions that have no link to Quilter

import Cookies from "js-cookie";
// Import these as necessary in your modules
//// Example usage:
// import utils from '../utils'
//// Then use as required:
// utils.toggleClass()
// utils.addClass()

function toggleClass(el: Element, classToToggle: string): void {
  const classN = el.className;
  if (classN.indexOf(classToToggle) > -1) {
    el.className = classN.replace(" " + classToToggle, "");
  } else {
    el.className = classN + " " + classToToggle;
  }
}
function addClass(el: Element, classToToggle: string): void {
  const classN = el.className;
  if (classN.indexOf(classToToggle) < 1) {
    el.className = classN + " " + classToToggle;
  }
}
function removeClass(el: Element, classToToggle: string): void {
  const classN = el.className;
  if (classN.indexOf(classToToggle) > -1) {
    el.className = classN.replace(" " + classToToggle, "");
  }
}
/* istanbul ignore next */
function getUrlParameters(): URLSearchParams {
  const url = new URL(window.location.href);
  return new URLSearchParams(url.search.slice(1));
}
/* istanbul ignore next */
function getUrlParameter(name: string): string | null {
  const params = getUrlParameters();
  return params ? params.get(name) : null;
}
/* istanbul ignore next */
function decodeParams(): any {
  let match;
  const pl = /\+/g, // Regex for replacing addition symbol with a space
    search = /([^&=]+)=?([^&]*)/g,
    decode = function (s) {
      return decodeURIComponent(s.replace(pl, " "));
    },
    query = window.location.search.substring(1),
    urlParams = {};

  while ((match = search.exec(query))) {
    urlParams[decode(match[1])] = decode(match[2]);
  }

  return urlParams;
}

// Thanks to https://stackoverflow.com/questions/1714786/query-string-encoding-of-a-javascript-object
/* istanbul ignore next */
function serializeParams(obj: any): string {
  const str: string[] = [];

  Object.keys(obj).forEach((key) => {
    if (obj[key] === null || obj[key] === undefined) {
      delete obj[key];
    }
  });

  // https://eslint.org/docs/rules/no-prototype-builtins
  for (const p in obj)
    if (Object.prototype.hasOwnProperty.call(obj, p)) {
      str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
    }
  return str.join("&");
}

// When dealing with boolean values coming from data/aria attributes, you can use this to convert to boolean
// To avoid doing comparisons on string like input === "true" etc.
function convertToBoolean(input?: string | boolean): boolean | undefined {
  if (typeof input === "undefined") {
    return undefined; // I.e. checking for a cookie that doesn't exist
  }
  if (typeof input === "boolean") {
    return input; // Just in case it already was a boolean
  }
  if (input.toLowerCase && input.toLowerCase() === "true") {
    return true;
  }
  if (input.toLowerCase && input.toLowerCase() === "false") {
    return false;
  }
  return undefined;
}

const isStringTrue = (prop?: string): boolean => {
  return !!prop && prop.toLowerCase() === "true";
};

const isANumber = (val: number): boolean => {
  return typeof val === "number" && !isNaN(val);
};

/* istanbul ignore next */
function renderIcon(name: string, cssClass = "", path = "/Content/images/v3/svg-sprite.svg"): string {
  return `
    <svg class="icon ${cssClass}" focusable="false">
      <use xlink:href="${path}#${name}" />
    </svg>`;
}

function formatDate(date: Date): string {
  return date.getDate() + "/" + (date.getMonth() + 1) + "/" + date.getFullYear();
}

function compareNumbers(a: number, b: number): number {
  return b - a;
}

/* istanbul ignore next */
function setCookie(name: string, val: string | number | boolean, expires: number, path = "/", secure = true): void {
  Cookies.set(name, val, { expires, path, secure });
}

function getRole(): string {
  return Cookies.get("Role");
}

function isCustomer(): boolean {
  return getRole() === "cust";
}

// Source https://bit.ly/2neWfJ2
/* istanbul ignore next */
const parseCookieValue = (str: string, char = ";"): any => {
  const parsedValue = str
    .split(char)
    .map((v) => v.split("="))
    .reduce((acc, v) => {
      acc[decodeURIComponent(v[0].trim())] = decodeURIComponent(v[1].trim());
      return acc;
    }, {});
  return parsedValue;
};

/* istanbul ignore next */
function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

const addDaysToDate = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

const getSortOrder = (params: Record<string, string | unknown>, settings: Record<string, string | unknown>) => {
  if (params.sortBy) {
    return params.sortBy as string;
  }

  if (params.query || params["input-autocomplete"]) {
    return 'rel';
  }

  return settings.sortBy as string;
}

const isFocusable = (el: HTMLElement) => {
  if (!el) return false;

  const focusableSelector =
    'a[href], area[href], input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), button:not([disabled]), iframe, object, embed, ' +
    '[tabindex]:not([tabindex="-1"]), [contenteditable]';

  return el.matches(focusableSelector) &&
    el.offsetParent !== null;
}

const findNextFocusableBySibling = (startEl: HTMLElement | null) => {
  if (!startEl) return null;

  let next = startEl.nextElementSibling as HTMLElement;
  while (next) {
    if (isFocusable(next)) {
      return next;
    }
    next = next.nextElementSibling as HTMLElement;
  }
  return null;
}


export default {
  toggleClass,
  addClass,
  removeClass,
  convertToBoolean,
  getUrlParameter,
  getUrlParameters,
  decodeParams,
  serializeParams,
  renderIcon,
  formatDate,
  compareNumbers,
  isANumber,
  isStringTrue,
  setCookie,
  parseCookieValue,
  isMobile,
  addDaysToDate,
  getRole,
  isCustomer,
  getSortOrder,
  isFocusable,
  findNextFocusableBySibling
};
